import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CalendarClock, MessageSquare, Phone, Mail, ChevronRight, Clock, CheckCircle, XCircle, RotateCcw, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  awaiting_type: { label: 'Awaiting Choice', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  awaiting_visit_reason: { label: 'Awaiting Reason', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  awaiting_datetime: { label: 'Awaiting Date/Time', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  awaiting_contact: { label: 'Awaiting Contact', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  rescheduling: { label: 'Rescheduling', color: 'bg-orange-500/20 text-orange-400', icon: RotateCcw },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

function ConversationDetail({ conversation, onClose, onCancel }) {
  if (!conversation) return null;
  const status = STATUS_CONFIG[conversation.status] || STATUS_CONFIG.awaiting_type;
  const StatusIcon = status.icon;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700/50 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="conversation-detail-modal">
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-sm">{conversation.customer_name}</h3>
              <p className="text-slate-400 text-xs mt-0.5">{conversation.vehicle}</p>
              <p className="text-slate-500 text-xs">{conversation.dealership_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`${status.color} text-[10px]`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
              {conversation.channel === 'sms' ? <Phone className="w-3.5 h-3.5 text-slate-400" /> : <Mail className="w-3.5 h-3.5 text-slate-400" />}
            </div>
          </div>
          {conversation.appointment_display && (
            <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs">
              <span className="text-slate-400">Appointment:</span>{' '}
              <span className="text-white font-medium">{conversation.appointment_type === 'test_drive' ? 'Test Drive' : 'Dealer Visit'} — {conversation.appointment_display}</span>
              {conversation.visit_reason && <span className="text-slate-400 ml-2">({conversation.visit_reason})</span>}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="conversation-messages">
          {(conversation.messages || []).map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[80%] p-3 rounded-lg text-xs leading-relaxed ${
                msg.role === 'assistant'
                  ? 'bg-slate-800 text-slate-200 border border-slate-700/50'
                  : 'bg-red-600/20 text-red-200 border border-red-700/30'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
                <p className="text-[9px] text-slate-500 mt-1.5">{new Date(msg.timestamp).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-slate-700/50 flex justify-between">
          {conversation.status !== 'cancelled' && conversation.status !== 'confirmed' && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-400 border-red-700/50 hover:bg-red-900/20 text-xs"
              onClick={() => onCancel(conversation.conversation_id)}
              data-testid="cancel-conversation-btn"
            >
              Cancel Conversation
            </Button>
          )}
          <Button variant="outline" size="sm" className="text-slate-400 border-slate-700 text-xs ml-auto" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function SchedulingPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dealershipFilter, setDealershipFilter] = useState('');
  const [dealerships, setDealerships] = useState([]);

  const fetchConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (dealershipFilter) params.append('dealership_id', dealershipFilter);
      params.append('limit', '50');
      const { data } = await axios.get(`${API}/api/scheduling/conversations?${params}`, { headers });
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dealershipFilter]);

  const fetchDealerships = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dealerships`, { headers });
      setDealerships(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch dealerships', err);
    }
  }, []);

  useEffect(() => { fetchDealerships(); }, [fetchDealerships]);
  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => { const interval = setInterval(fetchConversations, 30000); return () => clearInterval(interval); }, [fetchConversations]);

  const cancelConversation = async (conversationId) => {
    try {
      await axios.put(`${API}/api/scheduling/conversations/${conversationId}/cancel`, {}, { headers });
      toast.success('Conversation cancelled');
      fetchConversations();
      setSelectedConv(null);
    } catch (err) {
      toast.error('Failed to cancel conversation');
    }
  };

  const filtered = conversations.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.customer_name || '').toLowerCase().includes(q) ||
      (c.customer_email || '').toLowerCase().includes(q) ||
      (c.customer_phone || '').includes(q) ||
      (c.vehicle || '').toLowerCase().includes(q)
    );
  });

  const stats = {
    total: conversations.length,
    active: conversations.filter(c => !['confirmed', 'cancelled'].includes(c.status)).length,
    confirmed: conversations.filter(c => c.status === 'confirmed').length,
    cancelled: conversations.filter(c => c.status === 'cancelled').length,
  };

  return (
    <div className="space-y-4" data-testid="scheduling-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Appointment Scheduling</h1>
          <p className="text-xs text-slate-400 mt-0.5">Test drive and dealer visit conversations</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-white' },
          { label: 'Active', value: stats.active, color: 'text-blue-400' },
          { label: 'Confirmed', value: stats.confirmed, color: 'text-emerald-400' },
          { label: 'Cancelled', value: stats.cancelled, color: 'text-red-400' },
        ].map(s => (
          <div key={s.label} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color} mt-0.5`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            placeholder="Search by name, email, phone, vehicle..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 bg-slate-900/50 border-slate-800 text-white text-xs h-8"
            data-testid="scheduling-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-slate-900/50 border border-slate-800 text-slate-300 text-xs rounded-md h-8 px-2"
          data-testid="scheduling-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="awaiting_type">Awaiting Choice</option>
          <option value="awaiting_datetime">Awaiting Date/Time</option>
          <option value="awaiting_contact">Awaiting Contact</option>
          <option value="confirmed">Confirmed</option>
          <option value="rescheduling">Rescheduling</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={dealershipFilter}
          onChange={e => setDealershipFilter(e.target.value)}
          className="bg-slate-900/50 border border-slate-800 text-slate-300 text-xs rounded-md h-8 px-2"
          data-testid="scheduling-dealer-filter"
        >
          <option value="">All Dealerships</option>
          {dealerships.map(d => (
            <option key={d.dealership_id} value={d.dealership_id}>{d.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <CalendarClock className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No scheduling conversations yet</p>
          <p className="text-slate-600 text-xs mt-1">Enable scheduling in a dealership's Email Settings to start</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(conv => {
            const status = STATUS_CONFIG[conv.status] || STATUS_CONFIG.awaiting_type;
            const StatusIcon = status.icon;
            const lastMsg = conv.messages?.[conv.messages.length - 1];
            return (
              <div
                key={conv.conversation_id}
                className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 hover:border-slate-700 cursor-pointer transition-colors"
                onClick={() => setSelectedConv(conv)}
                data-testid={`conversation-${conv.conversation_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex-shrink-0">
                      {conv.channel === 'sms' ? <Phone className="w-4 h-4 text-slate-500" /> : <Mail className="w-4 h-4 text-slate-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium truncate">{conv.customer_name}</span>
                        <span className="text-slate-600 text-[10px]">{conv.dealership_name}</span>
                      </div>
                      <p className="text-slate-400 text-xs truncate">{conv.vehicle}</p>
                      {lastMsg && (
                        <p className="text-slate-500 text-[10px] truncate mt-0.5">
                          {lastMsg.role === 'assistant' ? 'Bot: ' : 'Customer: '}{lastMsg.content?.substring(0, 80)}...
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {conv.appointment_display && (
                      <span className="text-[10px] text-slate-400 hidden sm:block">{conv.appointment_display}</span>
                    )}
                    <Badge className={`${status.color} text-[10px]`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {status.label}
                    </Badge>
                    <span className="text-[10px] text-slate-600">{conv.messages?.length || 0} msgs</span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedConv && (
        <ConversationDetail
          conversation={selectedConv}
          onClose={() => setSelectedConv(null)}
          onCancel={cancelConversation}
        />
      )}
    </div>
  );
}
